import { parseAssetRopaWarningFlagsForFE } from 'config/api/ropaReviews/ropaReviews.parsers';

import { DPIA_CRITERIA_VERSION, DPIA_SCREENING_CRITERION_CODE_ALIASES, DPIA_TRIGGER_SOURCE_OPTIONS } from './dpiaRequests.constants';
import { calculateDpiaRecommendation } from './dpiaRequests.helpers';
import type {
  CancelDpiaRequestInput,
  DecideDpiaRequestInput,
  DpiaRecommendation,
  DpiaRequest,
  DpiaRequestContext,
  DpiaRequestContextDTO,
  DpiaRequestDecision,
  DpiaRequestDTO,
  DpiaRequestOpenResponse,
  DpiaRequestStatus,
  DpiaRequestTriggerSource,
  DpiaScreeningCriterionCode,
  OpenDpiaRequestInput,
  SaveDpiaRequestDraftInput,
} from './dpiaRequests.types';

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord => typeof value === 'object' && value !== null && !Array.isArray(value);

const EMPTY_WARNINGS = {
  dpia_indicated: false,
  dpia_required_unapproved: false,
  third_country_processing: false,
  sensitive_personal_data: false,
  confirmed_no_processing_activities: false,
};

export const EMPTY_DPIA_REQUEST_CONTEXT: DpiaRequestContext = {
  assetName: '—',
  assetProcessingActivityName: '—',
  assetOwnerName: null,
  dpiaIndicationStatus: 'not_indicated',
  previousRequestsCount: 0,
  previousDpiasCount: 0,
  warnings: parseAssetRopaWarningFlagsForFE(EMPTY_WARNINGS),
  centralProcessingActivity: {
    name: '—',
    description: '—',
    processingRole: '—',
    dataSubjectCategories: [],
    personalDataCategories: [],
    recipientCategories: [],
    minimumSecurityMeasures: [],
    purposes: [],
  },
  history: [],
  proposedDpiaTitle: '—',
};

const RECOMMENDATION_FROM_BE: Record<string, DpiaRecommendation> = {
  normally_not_recommended: 'normally_not_recommended',
  further_assessment_recommended: 'further_assessment_recommended',
  dpia_recommended: 'dpia_recommended',
  dpia_strongly_recommended: 'dpia_strongly_recommended',
  // Schema.yaml RecommendationEnum
  dpia_not_recommended: 'normally_not_recommended',
  dpia_may_be_recommended: 'further_assessment_recommended',
};

const RECOMMENDATION_TO_BE: Record<DpiaRecommendation, string> = {
  normally_not_recommended: 'dpia_not_recommended',
  further_assessment_recommended: 'dpia_may_be_recommended',
  dpia_recommended: 'dpia_recommended',
  dpia_strongly_recommended: 'dpia_strongly_recommended',
};

const asString = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback);

const asBoolean = (value: unknown, fallback = false): boolean => (typeof value === 'boolean' ? value : fallback);

const asNullableNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

const asNumber = (value: unknown, fallback = 0): number => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

const normalizeCriterionCode = (code: string): DpiaScreeningCriterionCode | null => {
  if ((Object.values(DPIA_SCREENING_CRITERION_CODE_ALIASES) as string[]).includes(code)) {
    return code as DpiaScreeningCriterionCode;
  }

  const aliased = DPIA_SCREENING_CRITERION_CODE_ALIASES[code];
  return aliased ?? null;
};

const parseSelectedCriteria = (dto: UnknownRecord): DpiaScreeningCriterionCode[] => {
  const raw = dto.selected_criteria ?? dto.criteria_codes;
  let codes: string[] = [];

  if (Array.isArray(raw)) {
    codes = raw.filter((item): item is string => typeof item === 'string');
  } else if (isRecord(raw)) {
    if (Array.isArray(raw.codes)) {
      codes = raw.codes.filter((item): item is string => typeof item === 'string');
    } else if (Array.isArray(raw.selected)) {
      codes = raw.selected.filter((item): item is string => typeof item === 'string');
    } else {
      codes = Object.entries(raw)
        .filter(([, enabled]) => enabled === true || enabled === 'true' || enabled === 1)
        .map(([code]) => code);
    }
  }

  return codes.map(normalizeCriterionCode).filter((code): code is DpiaScreeningCriterionCode => Boolean(code));
};

const parseTriggerSource = (value: unknown): DpiaRequestTriggerSource => {
  if (typeof value === 'string' && (DPIA_TRIGGER_SOURCE_OPTIONS as readonly string[]).includes(value)) {
    return value as DpiaRequestTriggerSource;
  }

  return 'ropa_administrator';
};

const parseRecommendation = (value: unknown, selectedCriteria: DpiaScreeningCriterionCode[], alwaysRule: boolean): DpiaRecommendation => {
  if (typeof value === 'string' && RECOMMENDATION_FROM_BE[value]) {
    return RECOMMENDATION_FROM_BE[value];
  }

  return calculateDpiaRecommendation(selectedCriteria, alwaysRule);
};

const parseDecisionFromStatus = (status: DpiaRequestStatus, explicitDecision: unknown): DpiaRequestDecision | null => {
  if (explicitDecision === 'dpia_required' || explicitDecision === 'dpia_not_required') {
    return explicitDecision;
  }

  if (status === 'decided_dpia_required') {
    return 'dpia_required';
  }

  if (status === 'decided_dpia_not_required') {
    return 'dpia_not_required';
  }

  return null;
};

const parseStatus = (value: unknown): DpiaRequestStatus => {
  if (value === 'draft' || value === 'decided_dpia_required' || value === 'decided_dpia_not_required' || value === 'cancelled') {
    return value;
  }

  return 'draft';
};

const parseCreatedDpiaForFE = (dto: NonNullable<DpiaRequestDTO['created_dpia']>) => ({
  id: dto.id,
  title: dto.title,
  status: dto.status,
  responsibleName: dto.responsible_name,
  createdAt: dto.created_at,
});

const toRequestDto = (payload: unknown): UnknownRecord => {
  if (!isRecord(payload)) {
    return {};
  }

  if (isRecord(payload.request)) {
    return payload.request;
  }

  return payload;
};

export const parseDpiaRequestForFE = (dtoInput: DpiaRequestDTO | UnknownRecord): DpiaRequest => {
  const dto = toRequestDto(dtoInput);
  const selectedCriteria = parseSelectedCriteria(dto);
  const alwaysDpiaRuleTriggered = asBoolean(dto.always_dpia_rule_triggered);
  const status = parseStatus(dto.status);
  const criteriaAssessmentComment = asString(dto.criteria_assessment_comment ?? dto.criteria_comment);
  const createdDpiaRaw = isRecord(dto.created_dpia) ? (dto.created_dpia as NonNullable<DpiaRequestDTO['created_dpia']>) : null;

  return {
    id: asNumber(dto.id),
    organizationId: asNumber(dto.organization_id),
    assetProcessingActivityId: asNumber(dto.asset_processing_activity_id),
    status,
    triggerSource: parseTriggerSource(dto.trigger_source),
    triggerComment: asString(dto.trigger_comment),
    criteriaVersion: asString(dto.criteria_version, DPIA_CRITERIA_VERSION),
    selectedCriteria,
    criteriaAssessmentComment,
    alwaysDpiaRuleTriggered,
    alwaysDpiaRuleComment: asString(dto.always_dpia_rule_comment),
    recommendation: parseRecommendation(dto.recommendation, selectedCriteria, alwaysDpiaRuleTriggered),
    decision: parseDecisionFromStatus(status, dto.decision),
    decisionComment: asString(dto.decision_comment),
    decisionDeviatesFromRecommendation: asBoolean(dto.decision_deviates_from_recommendation),
    assessedByMembershipId: asNullableNumber(dto.assessed_by_membership_id),
    assessedByName: typeof dto.assessed_by_name === 'string' ? dto.assessed_by_name : null,
    assessedAt: typeof dto.assessed_at === 'string' ? dto.assessed_at : null,
    createdDpiaId: asNullableNumber(dto.created_dpia_id),
    createdDpia: createdDpiaRaw ? parseCreatedDpiaForFE(createdDpiaRaw) : null,
    sourceRopaReviewRoundId: asNullableNumber(dto.source_ropa_review_round_id),
    sourceAssetProcessingActivityReviewId: asNullableNumber(dto.source_asset_processing_activity_review_id),
    cancellationComment: typeof dto.cancellation_comment === 'string' ? dto.cancellation_comment : null,
  };
};

export const parseDpiaRequestContextForFE = (dto: DpiaRequestContextDTO | UnknownRecord | undefined | null): DpiaRequestContext => {
  if (!isRecord(dto)) {
    return EMPTY_DPIA_REQUEST_CONTEXT;
  }

  const central = isRecord(dto.central_processing_activity) ? dto.central_processing_activity : null;
  const purposesRaw = central && Array.isArray(central.purposes) ? central.purposes : [];
  const historyRaw = Array.isArray(dto.history) ? dto.history : [];
  const warnings = isRecord(dto.warnings) ? dto.warnings : EMPTY_WARNINGS;

  const asStringList = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

  return {
    assetName: asString(dto.asset_name, EMPTY_DPIA_REQUEST_CONTEXT.assetName),
    assetProcessingActivityName: asString(dto.asset_processing_activity_name, EMPTY_DPIA_REQUEST_CONTEXT.assetProcessingActivityName),
    assetOwnerName: typeof dto.asset_owner_name === 'string' ? dto.asset_owner_name : null,
    dpiaIndicationStatus: asString(dto.dpia_indication_status, EMPTY_DPIA_REQUEST_CONTEXT.dpiaIndicationStatus),
    previousRequestsCount: asNumber(dto.previous_requests_count),
    previousDpiasCount: asNumber(dto.previous_dpias_count),
    warnings: parseAssetRopaWarningFlagsForFE({
      dpia_indicated: asBoolean(warnings.dpia_indicated),
      dpia_required_unapproved: asBoolean(warnings.dpia_required_unapproved),
      third_country_processing: asBoolean(warnings.third_country_processing),
      sensitive_personal_data: asBoolean(warnings.sensitive_personal_data),
      confirmed_no_processing_activities: asBoolean(warnings.confirmed_no_processing_activities),
    }),
    centralProcessingActivity: {
      name: asString(central?.name, EMPTY_DPIA_REQUEST_CONTEXT.centralProcessingActivity.name),
      description: asString(central?.description, EMPTY_DPIA_REQUEST_CONTEXT.centralProcessingActivity.description),
      processingRole: asString(central?.processing_role, EMPTY_DPIA_REQUEST_CONTEXT.centralProcessingActivity.processingRole),
      dataSubjectCategories: asStringList(central?.data_subject_categories),
      personalDataCategories: asStringList(central?.personal_data_categories),
      recipientCategories: asStringList(central?.recipient_categories),
      minimumSecurityMeasures: asStringList(central?.minimum_security_measures),
      purposes: purposesRaw.filter(isRecord).map(purpose => ({
        name: asString(purpose.name),
        legalBasis: asString(purpose.legal_basis),
        legalBasisComment: asString(purpose.legal_basis_comment),
        liaRequired: asBoolean(purpose.lia_required),
        retentionRule: asString(purpose.retention_rule),
      })),
    },
    history: historyRaw.filter(isRecord).map(item => ({
      id: asNumber(item.id),
      label: asString(item.label),
      subtitle: asString(item.subtitle),
      kind: item.kind === 'dpia' ? 'dpia' : 'request',
    })),
    proposedDpiaTitle: asString(dto.proposed_dpia_title, EMPTY_DPIA_REQUEST_CONTEXT.proposedDpiaTitle),
  };
};

export const parseDpiaRequestOpenResponseForFE = (dto: unknown): DpiaRequestOpenResponse => {
  const payload: UnknownRecord = isRecord(dto) ? dto : {};
  const nestedRequest = payload.request;
  const requestDto = isRecord(nestedRequest) ? nestedRequest : payload;

  let contextDto: UnknownRecord | null = null;
  if (isRecord(payload.context)) {
    contextDto = payload.context;
  } else if (isRecord(payload.request_context)) {
    contextDto = payload.request_context;
  }

  return {
    request: parseDpiaRequestForFE(requestDto),
    context: parseDpiaRequestContextForFE(contextDto),
  };
};

export const parseOpenDpiaRequestForBE = (payload: OpenDpiaRequestInput = {}) => ({
  ...(payload.sourceRopaReviewRoundId !== undefined ? { source_ropa_review_round_id: payload.sourceRopaReviewRoundId } : {}),
  ...(payload.sourceAssetProcessingActivityReviewId !== undefined
    ? { source_asset_processing_activity_review_id: payload.sourceAssetProcessingActivityReviewId }
    : {}),
});

const parseDraftPayloadForBE = (payload: SaveDpiaRequestDraftInput) => ({
  trigger_source: payload.triggerSource,
  trigger_comment: payload.triggerComment.trim(),
  // Runtime BE validates criteria_codes as a list of criterion codes (schema.yaml incorrectly types it as object).
  criteria_codes: payload.selectedCriteria,
  criteria_comment: payload.criteriaAssessmentComment.trim(),
  always_dpia_rule_triggered: payload.alwaysDpiaRuleTriggered,
  always_dpia_rule_comment: payload.alwaysDpiaRuleTriggered ? payload.alwaysDpiaRuleComment.trim() : '',
});

export const parseSaveDpiaRequestDraftForBE = (payload: SaveDpiaRequestDraftInput) => parseDraftPayloadForBE(payload);

export const parseDecideDpiaRequestForBE = (payload: DecideDpiaRequestInput) => ({
  ...parseDraftPayloadForBE(payload),
  decision_comment: payload.decisionComment.trim(),
});

export const parseCancelDpiaRequestForBE = (payload: CancelDpiaRequestInput) => ({
  cancellation_comment: payload.cancellationComment.trim(),
});

export const mapRecommendationForBE = (recommendation: DpiaRecommendation): string => RECOMMENDATION_TO_BE[recommendation];

export const applyDraftPayloadToRequest = (request: DpiaRequest, payload: SaveDpiaRequestDraftInput): DpiaRequest => {
  const recommendation = calculateDpiaRecommendation(payload.selectedCriteria, payload.alwaysDpiaRuleTriggered);

  return {
    ...request,
    triggerSource: payload.triggerSource,
    triggerComment: payload.triggerComment.trim(),
    selectedCriteria: payload.selectedCriteria,
    criteriaAssessmentComment: payload.criteriaAssessmentComment.trim(),
    alwaysDpiaRuleTriggered: payload.alwaysDpiaRuleTriggered,
    alwaysDpiaRuleComment: payload.alwaysDpiaRuleTriggered ? payload.alwaysDpiaRuleComment.trim() : '',
    recommendation,
  };
};
